/**
 * API Configuration
 * 
 * In development, API calls go through Vite proxy to localhost:8788
 * In production, API calls go to the same domain (playground.idnty.one)
 * 
 * The worker is deployed with zone routes that intercept /api/*, /saml/*, /oidc/*
 * requests on playground.idnty.one and route them to the worker.
 * This keeps the worker URL private and avoids CORS issues.
 */

// Base URL for the playground - used for all API and protocol endpoints
export const BASE_URL = import.meta.env.PROD 
  ? 'https://playground.idnty.one'
  : 'http://localhost:5173';

// Frontend URL - same as BASE_URL (for backward compatibility)
export const FRONTEND_URL = BASE_URL;

// Worker URL - now same as BASE_URL since zone routes handle the routing
export const WORKER_URL = BASE_URL;

/**
 * Get the API base URL
 * In development, use relative paths (handled by Vite proxy)
 * In production, use the same domain (zone routes handle /api/* routing)
 */
const getApiBaseUrl = () => {
  if (import.meta.env.PROD) {
    // Use same domain - zone routes intercept /api/* and route to worker
    return '';  // Empty string = same origin
  }
  // In development, use relative paths (handled by Vite proxy)
  return '';
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Build full API URL
 */
export function apiUrl(path) {
  const base = API_BASE_URL;
  if (!base) return path;
  return `${base}${path}`;
}

/**
 * Get SAML ACS URL
 * Zone routes intercept /saml/* and route to the worker
 */
export function getSamlAcsUrl() {
  return `${BASE_URL}/saml/acs`;
}

/**
 * Get SAML Metadata URL for a config
 */
export function getSamlMetadataUrl(configId) {
  return `${BASE_URL}/saml/metadata/${configId}`;
}

/**
 * Get SAML Login URL for a config
 */
export function getSamlLoginUrl(configId) {
  return `${BASE_URL}/saml/login/${configId}`;
}

/**
 * Get OIDC Callback URL
 * Zone routes intercept /oidc/* and route to the worker
 */
export function getOidcCallbackUrl() {
  return `${BASE_URL}/oidc/callback`;
}

/**
 * Get OIDC Authorization URL for a config
 */
export function getOidcAuthorizeUrl(configId) {
  return `${BASE_URL}/oidc/authorize/${configId}`;
}

/**
 * Default configuration values for new configs
 * All URLs use the same domain - zone routes handle routing to worker
 */
export const DEFAULT_CONFIG = {
  oidc: {
    redirectUri: getOidcCallbackUrl(),
  },
  saml: {
    sp: {
      entityId: 'https://playground.idnty.one/saml',
      acsUrl: getSamlAcsUrl(),
    },
  },
};

/**
 * Debug: Log configuration URLs
 */
export function logConfigUrls() {
  console.log('=== Playground IDNTY Configuration ===');
  console.log('Base URL:', BASE_URL);
  console.log('API Base URL:', API_BASE_URL);
  console.log('SAML ACS URL:', getSamlAcsUrl());
  console.log('OIDC Callback URL:', getOidcCallbackUrl());
  console.log('=====================================');
}

// Log URLs in development
if (import.meta.env.DEV) {
  logConfigUrls();
}
